import { DateFormat, formatDate, PurchaseItemStatus } from "common";
import { Dayjs } from "dayjs";
import { SeriesOptionsType } from "highcharts";
import { each } from "lodash";
import { GuestStatus, Seller } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { AnalyticsMode } from "../stores/reports/analyticsFilterStore";

const MAX_PRODUCTS_PER_CHART = 8;

export interface CustomersReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    mode: AnalyticsMode;
    products: ID[];
    showAllProducts?: boolean;
}

export const getCustomersReport = async (params: CustomersReportParams): Promise<SeriesOptionsType[]> => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.purchase;

    if (!params.from.isValid() || !params.to.isValid()) {
        return [];
    }

    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));

    return getReport(data, params.showAllProducts);
};

const getPayload = ({ seller, from, to, mode, products }: CustomersReportParams) => {
    const range = { from: formatDate(from, DateFormat.DATE_ISO), to: formatDate(to, DateFormat.DATE_ISO) };

    const filter = {
        size: 0,
        query: {
            bool: {
                must: [
                    { term: { "seller.id": seller.id } },
                    {
                        nested: {
                            path: "items",
                            query: {
                                bool: {
                                    must: { term: { "items.status": PurchaseItemStatus.CONFIRMED } },
                                    must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                                },
                            },
                        },
                    },
                ],
            },
        },
        aggs: {
            reserved_count: {
                nested: { path: "items" },
                aggs: {
                    status_filters: {
                        filter: {
                            bool: {
                                must: { term: { "items.status": PurchaseItemStatus.CONFIRMED } },
                                must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                            },
                        },
                        aggs: {
                            demographics: {
                                nested: { path: "items.lineItems" },
                                aggs: {
                                    demographics_filtered: {
                                        filter: {
                                            bool: {
                                                must: [{ term: { "items.lineItems.type": "demographic" } }],
                                            },
                                        },
                                        aggs: {
                                            total_count: {
                                                sum: { field: "items.lineItems.quantity" },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    } as any;

    const queryFilter = filter.query.bool.must;
    const { aggs } = filter;

    if (mode === AnalyticsMode.ARRIVAL_DATE) {
        filter.aggs.on_nested_items = getArrivalNestedItems(products, range);

        const rangeFilter = {
            range: {
                "items.arrivalDate": {
                    gte: range.from,
                    lte: range.to,
                },
            },
        };

        const queryRangeFilter = {
            nested: {
                path: "items",
                query: {
                    bool: {
                        must: rangeFilter,
                    },
                },
            },
        };

        aggs.on_nested_items.aggs.arrival_filter.aggs.demographics_over_time.date_histogram = {
            field: "items.arrivalDate",
            interval: "day",
        };

        queryFilter.push(queryRangeFilter);
    } else {
        filter.aggs.demographics_over_time = getDemographicOverTimePurchase(products);
        const rangeFilter = {
            range: {
                createdAt: {
                    gte: range.from,
                    lte: range.to,
                    time_zone: seller.timezoneName,
                },
            },
        };
        queryFilter.push(rangeFilter);

        aggs.demographics_over_time.date_histogram = {
            field: "createdAt",
            interval: "day",
            time_zone: seller.timezoneName,
        };
    }

    return filter;
};

const getProductFilters = (products: ID[]) => {
    const filter: any = { filters: { all: { match_all: { boost: 1 } } } };

    // Create a date_histogram for each product only if there are <= 8 products
    if (products.length <= MAX_PRODUCTS_PER_CHART) {
        for (const productId of products) {
            filter.filters[productId] = { term: { "items.product.id": productId } };
        }
    } else {
        filter.filters.selected = { terms: { "items.product.id": products } };
    }

    return filter;
};

const getReport = (resp: any, showAllProducts?: boolean) => {
    if (!resp.aggregations) {
        return [];
    }

    const seriesData: any = [];

    const isArrival = !!resp.aggregations.on_nested_items;

    const buckets = isArrival
        ? resp.aggregations.on_nested_items.arrival_filter.demographics_over_time.buckets
        : resp.aggregations.demographics_over_time.buckets;

    const dates: string[] = [];

    each(buckets, (bucket: any) => {
        dates.push(bucket.key_as_string);
        const data = getBucketData(bucket, isArrival, showAllProducts);
        seriesData.push(...data);
    });

    const group: any = {};

    for (const { name, data, demographicBreakdown } of seriesData) {
        group[name] = group[name] || { name, data: [], demographicBreakdown: [] };
        group[name].data.push(data);
        group[name].demographicBreakdown.push(demographicBreakdown);
    }

    const combinedSeriesData: any = Object.values(group);
    combinedSeriesData.dates = dates;
    return combinedSeriesData;
};

const getBucketData = (s: any, isArrival: boolean, showAllProducts?: boolean) => {
    const buckets = isArrival
        ? s.status_filters.filter_and_agg.buckets
        : s.on_nested_items.status_filters.filter_and_agg.buckets;

    const seriesItem: any = [];
    each(buckets, (r: any, k: string) => {
        if (k !== "all" || showAllProducts) {
            seriesItem.push({
                name: k,
                data: r.on_nested_demo.demographics_filtered.total_count.value || 0,
                demographicBreakdown: r.on_nested_demo.demographics_filtered?.labels?.buckets || [],
            });
        }
    });

    return seriesItem;
};

const getDemographicSFAggs = (products: ID[]) => {
    return {
        filter_and_agg: {
            filters: getProductFilters(products),
            aggs: {
                on_nested_demo: {
                    nested: { path: "items.lineItems" },
                    aggs: {
                        demographics_filtered: {
                            filter: {
                                bool: {
                                    must: [{ term: { "items.lineItems.type": "demographic" } }],
                                },
                            },
                            aggs: {
                                labels: {
                                    terms: {
                                        field: "items.lineItems.name",
                                        order: { quantity_count: "desc" },
                                    },
                                    aggs: {
                                        quantity_count: {
                                            sum: { field: "items.lineItems.quantity" },
                                        },
                                    },
                                },
                                total_count: {
                                    sum: { field: "items.lineItems.quantity" },
                                },
                            },
                        },
                    },
                },
            },
        },
    };
};

function getDemographicOverTimePurchase(products: ID[]) {
    return {
        date_histogram: {},
        aggs: {
            on_nested_items: {
                nested: { path: "items" },
                aggs: {
                    status_filters: {
                        filter: {
                            bool: {
                                must: { term: { "items.status": PurchaseItemStatus.CONFIRMED } },
                                must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                            },
                        },
                        aggs: getDemographicSFAggs(products),
                    },
                },
            },
        },
    };
}

function getDemographicOverTimeArrival(products: ID[]) {
    return {
        demographics_over_time: {
            date_histogram: {},
            aggs: {
                status_filters: {
                    filter: {
                        bool: {
                            must: { term: { "items.status": PurchaseItemStatus.CONFIRMED } },
                            must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                        },
                    },
                    aggs: getDemographicSFAggs(products),
                },
            },
        },
    };
}

function getArrivalNestedItems(products: ID[], range: any) {
    return {
        nested: {
            path: "items",
        },
        aggs: {
            arrival_filter: {
                filter: {
                    bool: {
                        must: [
                            {
                                range: {
                                    "items.arrivalDate": {
                                        gte: range.from,
                                        lte: range.to,
                                    },
                                },
                            },
                        ],
                    },
                },
                aggs: getDemographicOverTimeArrival(products),
            },
        },
    };
}
